import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_worldLow from "@amcharts/amcharts4-geodata/worldLow";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

interface CompanyMapChartProps {
  latitude: number;
  longitude: number;
  companyName: string;
}

export const CompanyMapChart: React.FC<CompanyMapChartProps> = ({
  latitude,
  longitude,
  companyName,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<am4maps.MapChart | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Create map instance
    const chart = am4core.create(mapRef.current, am4maps.MapChart);
    chartRef.current = chart;

    // Set map definition
    chart.geodata = am4geodata_worldLow;

    // Center map on company location
    chart.homeZoomLevel = 7;
    chart.homeGeoPoint = {
      latitude,
      longitude,
    };

    // Set projection
    chart.projection = new am4maps.projections.Miller();

    // Create map polygon series
    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;
    polygonSeries.exclude = ["AQ"];
    polygonSeries.mapPolygons.template.fill = am4core.color("#DFDFDF");
    polygonSeries.mapPolygons.template.stroke = am4core.color("#FFFFFF");
    polygonSeries.mapPolygons.template.strokeWidth = 0.5;

    const polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.tooltipText = "{name}";

    const hs = polygonTemplate.states.create("hover");
    hs.properties.fill = am4core.color("#B0B0B0"); // Darker gray - subtle but clear

    // Create image series for markers
    const imageSeries = chart.series.push(new am4maps.MapImageSeries());

    const place = imageSeries.mapImages.template;
    place.latitude = latitude;
    place.longitude = longitude;
    imageSeries.data = [
      {
        name: companyName,
        hcenter: "center",
        vcenter: "bottom",
      },
    ];

    const pin = place.createChild(am4core.Sprite);
    pin.path =
      "M12,2C8.13,2 5,5.13 5,9c0,5.25 7,13 7,13s7,-7.75 7,-13C19,5.13 15.87,2 12,2zM12,11.5c-1.38,0 -2.5,-1.12 -2.5,-2.5s1.12,-2.5 2.5,-2.5s2.5,1.12 2.5,2.5S13.38,11.5 12,11.5z";
    pin.fill = am4core.color("#FF6B6B");
    pin.stroke = am4core.color("#FFFFFF");
    pin.strokeWidth = 2;
    pin.horizontalCenter = "middle";
    pin.verticalCenter = "bottom";
    pin.nonScaling = true;

    const label = place.createChild(am4core.Label);
    label.padding(15, 15, 15, 15);
    label.text = companyName;
    label.dy = -1;
    label.verticalCenter = "middle";
    label.nonScaling = true;

    chart.smallMap = new am4maps.SmallMap();
    chart.smallMap.series.push(polygonSeries);

    return () => {
      // Async disposal to prevent UI blocking
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.dispose();
          chartRef.current = null;
        }
      }, 0);
    };
  }, [latitude, longitude, companyName]);

  return (
    <Box sx={{ mt: 3, height: 260, width: "100%" }}>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
    </Box>
  );
};
